import { useEffect, useState } from 'react';

import { Box,Stack, Switch, Typography } from '@mui/material';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { useAppDispatch, useAppSelector } from 'app/hooks';
import { AppSuiteSearchableDropDown } from 'common/components/AppSuiteSearchableDropDown';
import { Page } from 'common/components/Page';
import { PalmDatePicker } from 'common/components/sharedCustomComponents/PalmDatePicker/PalmDatePicker';
import { StyledBreadcrumbs } from 'common/components/StyledBreadcrumbs';
import { TenantSearchableDropDown } from 'common/components/TenantSearchableDropDown';
import {
    DEFAULT_TENANT,
    USERS_WITH_NEW_ACCESS_REPORT_SLUG,
    USERS_WITH_NEW_ACCESS_REPORT_TITLE } from 'common/constants/reports';
import { UsersWithNewAccessReportRequest } from 'common/services/api/Reports/UsersWithNewAccess.api';
import { AclResource, ApplicationSuite,AppSuiteTenant } from 'common/types/Entities';
import dayjs from 'dayjs';
import { useSnackbar } from 'notistack';

import { useGenerateDownloadableCSV } from '../../hooks/common/useGenerateDownloadableCSV';
import { useFetchUsersWithNewAccessReport } from '../../hooks/useFetchUsersWithNewAccessReport';
import {
    selectIsTenantDropDownDisabled,
    setTenantDropDownDisabled } from '../../tenantSearchableDropDownSlice';
import { AclMultipleSelectDropDown } from '../common/AclMultipleSelectDropDown';
import { CsvReportExportPopupLoader } from '../common/CsvReportExportPopupLoader';
import { ReportFooterButtons } from '../common/ReportFooterButtons';

import { generateReportFilename, normalizeReportData } from './usersWithNewAccessReportUtils';

const USERS_WITH_NEW_ACCESS_REPORT_ERROR_MESSAGE_PREFIX = 'There was an error while generating New Users Access CSV Report:';
const DEFAULT_POPUP_LOADER_MESSAGE = 'Exporting...';

export function UsersWithNewAccessReport() {
    const dispatch = useAppDispatch();
    const { enqueueSnackbar } = useSnackbar();

    const [appSuite, setAppSuite] = useState<ApplicationSuite | null>(null);
    const [tenant, setTenant] = useState<AppSuiteTenant | null>(null);
    const [aclResources, setAclResources] = useState<AclResource[]>([]);
    const [startingFrom, setStartingFrom] = useState<string>('');
    const [upUntil, setUpUntil] = useState<string>('');
    const [reportType, setReportType] = useState<'acl' | 'utm'>('acl');

    const isTenantSearchableDropDownDisabled = useAppSelector(selectIsTenantDropDownDisabled);

    const { processing, processFetchNewUsersAccessReportData } = useFetchUsersWithNewAccessReport();
    const { convertToCSV } = useGenerateDownloadableCSV('NewUsersAccessReport');

    useEffect(() => {
        dispatch(setTenantDropDownDisabled(true));
    }, [dispatch]);

    useEffect(() => {
        if (appSuite) {
            dispatch(setTenantDropDownDisabled(false));
        } else {
            dispatch(setTenantDropDownDisabled(true));
        }
    }, [appSuite, dispatch]);

    const handleApplicationSuiteChange = (_e: unknown, newAppSuite: ApplicationSuite | null) => {
        setAppSuite(newAppSuite);
        setTenant(null);
        setAclResources([]);
    };

    const handleTenantChange = (_e: unknown, newTenant: AppSuiteTenant | null) => {
        setTenant(newTenant);
        setAclResources([]);
    };

    const handleAclResourcesChange = (newAclResources: AclResource[]) => {
        setAclResources(newAclResources);
    };

    const handleStartingFromChange = (value: string) => {
        setStartingFrom(value);
    };

    const handleUpUntilChange = (value: string) => {
        setUpUntil(value);
    };

    const handleReportTypeToggle = (event: React.ChangeEvent<HTMLInputElement>) => {
        setReportType(event.target.checked ? 'utm' : 'acl');
    };

    const handleOpenReportModal = async () => {
        if (!startingFrom || !upUntil) {
            enqueueSnackbar('Please select both date range values', { variant: 'error' });
            return;
        }
        if (!tenant?.id) {
            enqueueSnackbar('Please select a tenant', { variant: 'error' });
            return;
        }
        try {
            const request: UsersWithNewAccessReportRequest = {
                startDate: dayjs(startingFrom).format('YYYY-MM-DD'),
                endDate: dayjs(upUntil).format('YYYY-MM-DD'),
                resourcesIds: aclResources.map(resource => resource.id)
            };
            const reportData = await processFetchNewUsersAccessReportData(tenant.id, request, reportType);

            // Normalize the data to ensure all required columns are present
            const normalizedData = normalizeReportData(reportData);

            const csv = await convertToCSV(normalizedData);

            // Generate dynamic filename with app suite, tenant, and timestamp
            const dynamicFilename = generateReportFilename(
                appSuite?.name ?? 'UnknownAppSuite',
                tenant?.name ?? 'UnknownTenant'
            );

            // Create custom download with dynamic filename
            const csvFile = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
            const csvURL = globalThis.URL.createObjectURL(csvFile);
            const tempLink = document.createElement('a');
            tempLink.href = csvURL;
            tempLink.setAttribute('download', `${dynamicFilename}.csv`);
            tempLink.click();

            // Clean up the URL object
            globalThis.URL.revokeObjectURL(csvURL);
        } catch (error: unknown) {
            const err = error as Error;
            enqueueSnackbar(
                `${USERS_WITH_NEW_ACCESS_REPORT_ERROR_MESSAGE_PREFIX} ${err.message}`,
                { variant: 'error' }
            );
        }
    };

    const disableGenerateButton = !appSuite || !tenant || !startingFrom || !upUntil;

    return (
        <Page
            title={`${USERS_WITH_NEW_ACCESS_REPORT_TITLE} Report`}
            header={(
                <Typography variant="h5" gutterBottom component="div">
                    <StyledBreadcrumbs
                        numberOfTabsToExcludeFromLinks={1}
                        guidNameMappings={[{
                            resourceName: USERS_WITH_NEW_ACCESS_REPORT_TITLE,
                            resourceId: USERS_WITH_NEW_ACCESS_REPORT_SLUG
                        }]}
                    />
                    {`${USERS_WITH_NEW_ACCESS_REPORT_TITLE} Report`}
                </Typography>
            )}
        >
            <Stack
                sx={{ paddingTop: '1rem', width: '30%' }}
                spacing={2}
            >
                <AppSuiteSearchableDropDown
                    value={appSuite}
                    onChange={handleApplicationSuiteChange}
                />
                <TenantSearchableDropDown
                    appSuite={appSuite}
                    value={tenant ?? DEFAULT_TENANT}
                    onChange={handleTenantChange}
                    disabled={isTenantSearchableDropDownDisabled}
                />
                <AclMultipleSelectDropDown
                    tenantId={tenant?.id ?? ''}
                    value={aclResources}
                    onChange={handleAclResourcesChange}
                />
                <Typography
                    variant="caption"
                    sx={{
                        color: 'text.secondary',
                        fontStyle: 'italic',
                        marginTop: '-8px',
                        marginBottom: '8px'
                    }}
                >
                    {aclResources.length === 0
                        ? '⚠️ No ACL resources selected - all ACL resources will be included by default'
                        : `${aclResources.length} ACL resource(s) selected`
                    }
                </Typography>
                <LocalizationProvider dateAdapter={AdapterDayjs}>
                    <Stack display="flex" direction="row" justifyContent="space-between" spacing={2}>
                        <PalmDatePicker
                            label='Starting From'
                            value={startingFrom}
                            onChange={handleStartingFromChange}
                        />
                        <PalmDatePicker
                            label='Up Until'
                            value={upUntil}
                            onChange={handleUpUntilChange}
                        />
                    </Stack>
                </LocalizationProvider>
                <Box
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1,
                        padding: 1
                    }}
                >
                    <Typography
                        variant="body2"
                        sx={{
                            color: reportType === 'acl' ? 'text.primary' : 'text.disabled',
                            fontWeight: reportType === 'acl' ? 'bold' : 'normal'
                        }}
                    >
                        By ACL
                    </Typography>
                    <Switch
                        checked={reportType === 'utm'}
                        onChange={handleReportTypeToggle}
                        size="small"
                    />
                    <Typography
                        variant="body2"
                        sx={{
                            color: reportType === 'utm' ? 'text.primary' : 'text.disabled',
                            fontWeight: reportType === 'utm' ? 'bold' : 'normal'
                        }}
                    >
                        By UTM
                    </Typography>
                </Box>
                <ReportFooterButtons
                    handleOpenReportModal={handleOpenReportModal}
                    disableGenerateButton={disableGenerateButton}
                />
            </Stack>
            <CsvReportExportPopupLoader
                open={processing}
                title={DEFAULT_POPUP_LOADER_MESSAGE}
            />
        </Page>
    );
}
